/**
 * Admin Withdrawals API Service
 */

import { get, patch } from "../client";

export interface AdminWithdrawal {
  id: string;
  partnerId: string;
  amount: number;
  bankName: string;
  bankAccountNumber: string;
  bankAccountName: string;
  status: "pending" | "processing" | "completed" | "rejected";
  notes: string | null;
  adminNotes: string | null;
  processedBy: string | null;
  processedAt: string | null;
  createdAt: string;
  updatedAt: string;
  partner: {
    id: string;
    tier: string;
    user: {
      id: string;
      fullName: string;
      email: string;
      phone: string;
    };
  };
}

export interface WithdrawalStats {
  pending: { count: number; amount: number };
  processing: { count: number; amount: number };
  completed: { count: number; amount: number };
}

interface WithdrawalsResponse {
  success: boolean;
  data: any;
}

interface WithdrawalListPayload {
  meta?: {
    total?: number;
    perPage?: number;
    currentPage?: number;
    lastPage?: number;
  };
  data?: any[];
}

interface WithdrawalMeta {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
}

function toAdminWithdrawal(row: any): AdminWithdrawal {
  return {
    id: String(row.id || ""),
    partnerId: String(row.partnerId || row.partner_id || ""),
    amount: Number(row.amount || 0),
    bankName: String(row.bankName || row.bank_name || "-"),
    bankAccountNumber: String(
      row.bankAccountNumber || row.bank_account_number || "-"
    ),
    bankAccountName: String(row.bankAccountName || row.bank_account_name || "-"),
    status: (row.status || "pending") as AdminWithdrawal["status"],
    notes: row.notes ?? null,
    adminNotes: row.adminNotes ?? row.admin_notes ?? null,
    processedBy: row.processedBy ?? row.processed_by ?? null,
    processedAt: row.processedAt ?? row.processed_at ?? null,
    createdAt: String(row.createdAt || row.created_at || new Date().toISOString()),
    updatedAt: String(row.updatedAt || row.updated_at || new Date().toISOString()),
    partner: {
      id: String(row.partner?.id || row.partner_id || ""),
      tier: String(row.partner?.tier || "-"),
      user: {
        id: String(row.partner?.user?.id || row.user_id || ""),
        fullName: String(
          row.partner?.user?.fullName || row.partner_name || "-"
        ),
        email: String(row.partner?.user?.email || ""),
        phone: String(row.partner?.user?.phone || ""),
      },
    },
  };
}

function normalizeWithdrawalList(
  payload: any,
  page: number
): { data: AdminWithdrawal[]; meta: WithdrawalMeta } {
  if (Array.isArray(payload)) {
    const data = payload.map(toAdminWithdrawal);
    return {
      data,
      meta: {
        total: data.length,
        perPage: data.length || 20,
        currentPage: page,
        lastPage: 1,
      },
    };
  }

  const objectPayload = (payload || {}) as WithdrawalListPayload;
  const rows = Array.isArray(objectPayload.data) ? objectPayload.data : [];
  const data = rows.map(toAdminWithdrawal);
  const meta = objectPayload.meta || {};

  return {
    data,
    meta: {
      total: Number(meta.total || data.length || 0),
      perPage: Number(meta.perPage || data.length || 20),
      currentPage: Number(meta.currentPage || page),
      lastPage: Number(meta.lastPage || 1),
    },
  };
}

interface LegacyWithdrawalsResponse {
  success: boolean;
  data: {
    meta: {
      total: number;
      perPage: number;
      currentPage: number;
      lastPage: number;
    };
    data: AdminWithdrawal[];
  };
}

interface WithdrawalResponse {
  success: boolean;
  message: string;
  data: AdminWithdrawal;
}

interface StatsResponse {
  success: boolean;
  data: WithdrawalStats;
}

/**
 * Get all withdrawals with pagination
 */
export async function getWithdrawals(
  page = 1,
  status = ""
): Promise<{ data: AdminWithdrawal[]; meta: any }> {
  const params = new URLSearchParams();
  params.append("page", String(page));
  if (status) params.append("status", status);

  const response = await get<WithdrawalsResponse | LegacyWithdrawalsResponse>(
    `/admin/withdrawals?${params.toString()}`
  );
  return normalizeWithdrawalList((response as any).data, page);
}

/**
 * Get withdrawal by ID
 */
export async function getWithdrawal(id: string): Promise<AdminWithdrawal> {
  const response = await get<{ success: boolean; data: any }>(
    `/admin/withdrawals/${id}`
  );
  return toAdminWithdrawal((response as any).data || {});
}

/**
 * Process a withdrawal (approve/reject)
 */
export async function processWithdrawal(
  id: string,
  data: { status: "processing" | "completed" | "rejected"; adminNotes?: string }
): Promise<AdminWithdrawal> {
  const response = await patch<WithdrawalResponse>(
    `/admin/withdrawals/${id}/process`,
    data
  );
  return response.data;
}

/**
 * Get withdrawal statistics
 */
export async function getWithdrawalStats(): Promise<WithdrawalStats> {
  const response = await get<StatsResponse>("/admin/withdrawals/stats");
  return response.data;
}
